import './csp-nonce'
import 'react-app-polyfill/ie11' // eslint-disable-line import-x/no-extraneous-dependencies
/**
 * Current the application does NOT work on IE11. These polyfills enable the application
 * to display an message, requesting the user to switch to Chrome. The application itself
 * does not work.
 *
 * When core-js is imported, the application gives a stack overflow in IE11. When the includes
 * below are included, IE11 will complain that the iterator symbol is not available:
 * `Object doesn't support property or method 'Symbol(Symbol.iterator`
 */
import { createRoot } from 'react-dom/client'
import qs from 'qs'

import { AxiosResponse } from 'axios'
import { dataLayerPublish } from '@dhl/datalayer'

import { App } from './App'
import { store } from './redux/store'
import { initializeBasket, addShipment } from './basket/model/session/actions'
import { Config } from './basket/model/session/reducers'
import { getCountryCodeByCurrentDomain } from './util/domain/getCountryCodeByCurrentDomain'
import { getCarrierByDomain } from './util/domain/getCarrierByDomain'
import { checkLoggedIn } from './account/model/util/service'
import { expired, initialLoggedInUser, initialUserCheckDone } from './account/model/actions'
import './i18n'
import './setLocationHref'
import { ParcelShopService } from './account/model/reducer'
import { loadPolyfills } from './loadPolyfills'
import { getConfig } from './config'
import { restoreFromSessionState } from './basket/model/actions'
import { PAYMENT_FAILURE } from './basket/model/constants'
import { onOtcPath } from './paths'

const { cspNonce } = getConfig()

// @ts-ignore
// eslint-disable-next-line camelcase
__webpack_nonce__ = cspNonce

/** ****************************************************************************
 * Additional Typescript declarations
 **************************************************************************** */

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace NodeJS {
    interface Global {
      location?: { hostname?: string }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      localStorage: any
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      sessionStorage: any
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      __REDUX_DEVTOOLS_EXTENSION__: any
    }
  }
}

/** ****************************************************************************
 * Launch app
 **************************************************************************** */

const query = qs.parse(window.location.search, { ignoreQueryPrefix: true, allowDots: true })

const sanitize = (input: string) => {
  if (!input) {
    return undefined
  }

  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;',
  }
  const reg = /[&<>"'/]/
  // @ts-ignore
  return input.replace(reg, (match) => map[match])
}

const render = () => {
  const originCountry = getCountryCodeByCurrentDomain()

  // eslint-disable-next-line @typescript-eslint/no-shadow, @typescript-eslint/no-explicit-any
  const queryParams = (query: any) => ({
    defaultCarrier: sanitize(query.carrierOption as string) || getCarrierByDomain(),
    defaultDestinationCountry:
      sanitize(query.package_tocountrycode as string) || sanitize(query.destinationCountry) || originCountry,
    preFilledParcelTypeKey: sanitize(query.parcelTypeKey) || null,
    preFilledReceiver: {
      firstName: sanitize(query.receiver?.firstName as string) || '',
      lastName: sanitize(query.receiver?.lastName as string) || '',
      postalCode: sanitize(query.receiver?.postalCode as string) || '',
      houseNumber: sanitize(query.receiver?.houseNumber as string) || '',
      houseNumberSuffix: sanitize(query.receiver?.houseNumberSuffix as string) || '',
      email: sanitize(query.receiver?.email as string) || '',
      phoneNumber: sanitize(query.receiver?.phoneNumber as string) || '',
      deliveryOptionKey: sanitize(query.receiver?.deliveryOptionKey) || null,
    },
    preFilledShipper: {
      firstName: sanitize(query.shipper?.firstName as string) || '',
      lastName: sanitize(query.shipper?.lastName as string) || '',
      postalCode: sanitize(query.shipper?.postalCode as string) || '',
      houseNumber: sanitize(query.shipper?.houseNumber as string) || '',
      houseNumberSuffix: sanitize(query.shipper?.houseNumberSuffix as string) || '',
      email: sanitize(query.shipper?.email as string) || '',
      phoneNumber: sanitize(query.shipper?.phoneNumber as string) || '',
      companyName: sanitize(query.shipper?.companyName as string) || '',
      vatNumber: sanitize(query.shipper?.vatNumber as string) || '',
    },
  })

  const defaultWeight = query.weight || null

  const config: Partial<Config> = {
    ...queryParams(query),
  }

  const container = document.getElementById('root')
  const root = createRoot(container!)

  // dispatch actions based on user
  checkLoggedIn()
    .then(({ user }) => {
      if (user) {
        const isSpaServiceAvailable = user?.parcelShopServices.includes(ParcelShopService.SPA) ?? false
        store.dispatch(initializeBasket(originCountry, { ...config, isSpaServiceAvailable }))
        store.dispatch(addShipment(defaultWeight ? +defaultWeight : null))
        store.dispatch(initialLoggedInUser(user))
        store.dispatch(initialUserCheckDone())
      } else {
        store.dispatch(expired())
      }
    })
    .catch((error) => {
      if (error instanceof Error) {
        dataLayerPublish('ERROR', {
          id: 'checkLoggedIn',
          error,
        })
        store.dispatch(expired())
      } else {
        const axiosResponse = error as AxiosResponse
        if (axiosResponse.status === 403 && onOtcPath()) {
          // User is not allowed to be on the OTC path when not logged in
          store.dispatch(expired())
        } else {
          // User is allowed on the PWA path when not logged in
          store.dispatch(initializeBasket(originCountry, config))
          store.dispatch(addShipment(defaultWeight ? +defaultWeight : null))
          store.dispatch(initialUserCheckDone())
        }
      }
    })

  // Render application
  root.render(<App />)

  if (window.location.href.includes(PAYMENT_FAILURE)) {
    store.dispatch(restoreFromSessionState())
  }
}

loadPolyfills().then(() => render())
